import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {API_BASE_URL, BASE_URL, STUDY_PLAN_API} from "../config/api";
import "../styles.css";

const PublishForm = ({
  exerciseId,
  exerciseName,
  exerciseType,
  exerciseImage,
  onClose,
  onPublishSuccess,
  initialCheckedState,
}) => {
  const [publishSettings, setPublishSettings] = useState({
    degree: "3/4",
    category: "",
    domain: "",
    sub_category: "",
    sub_sub_category: "",
    sub_sub_sub_category: "",
    exercise_url: "",
    preview: "",
    objective: "",
  });
  const [categoriesData, setCategoriesData] = useState({
    categories: [],
  });
  const [loading, setLoading] = useState(true);
  const [publishLoading, setPublishLoading] = useState(false);
  const [error, setError] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [isChecked, setIsChecked] = useState(initialCheckedState || false);

  const fetchCategories = async (degree) => {
    try {
      // Temporary development solution - bypass SSL verification
      process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';

      const response = await fetch(STUDY_PLAN_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ degree }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || "Failed to fetch categories"}`);
      }

      const data = await response.json();
      setCategoriesData({ categories: Array.isArray(data) ? data : [] });
      setLoading(false);
    } catch (err) {
      console.error("Fetch Error (Categories):", err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchCategories(publishSettings.degree);
  }, [publishSettings.degree]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    fetchCategories(publishSettings.degree);
  };

  const getSubCategories = (parentPath, level) => {
    let current = categoriesData.categories;
    const pathParts = parentPath.split(".").filter(Boolean);

    for (let i = 0; i < pathParts.length && current; i++) {
      if (Array.isArray(current)) {
        current = current.find((item) => item.title === pathParts[i]);
      } else if (current.sub_categories) {
        current = current.sub_categories.find((item) => item.title === pathParts[i]);
      } else {
        return [];
      }
    }

    const subCategories = current && current.sub_categories ? current.sub_categories : [];
    return subCategories.map((item) => ({
      title: item.title || "Unnamed",
      hasSubCategories: !!item.sub_categories && item.sub_categories.length > 0,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPublishSettings((prev) => {
      const resetFields = {};
      if (name === "category") {
        resetFields.sub_category = "";
        resetFields.sub_sub_category = "";
        resetFields.sub_sub_sub_category = "";
        const selectedCategory = categoriesData.categories.find(
          (cat) => cat.title === value
        );
        resetFields.domain = selectedCategory?.domain || "";
      } else if (name === "sub_category") {
        resetFields.sub_sub_category = "";
        resetFields.sub_sub_sub_category = "";
      } else if (name === "sub_sub_category") {
        resetFields.sub_sub_sub_category = "";
      }
      return { ...prev, ...resetFields, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPublishLoading(true);
    setPublishError(null);
    setIsChecked(true); // Set checked state when publish begins

    try {
      const payload = {
        exerciseId,
        degree: publishSettings.degree,
        category: publishSettings.category,
        domain: publishSettings.domain,
        sub_category: publishSettings.sub_category,
        sub_sub_category: publishSettings.sub_sub_category,
        sub_sub_sub_category: publishSettings.sub_sub_sub_category,
        exercise_url: publishSettings.exercise_url || `${BASE_URL}/exercise/${exerciseId}`,
        preview: publishSettings.preview || "Default preview description",
        objective: publishSettings.objective,
      };

      const response = await fetch(`${API_BASE_URL}/storePublishedExercise`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to publish exercise");
      }

      const data = await response.json();
      Swal.fire({
        title: "Published!",
        text: "The exercise has been published successfully.",
        icon: "success",
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
      });

      if (onPublishSuccess) onPublishSuccess();
      onClose();
    } catch (error) {
      console.error("Publish Error:", error);
      setIsChecked(false); // Reset checked state on error
      Swal.fire({
        title: "Error!",
        text: error.message.includes("already")
          ? "Exercise already published"
          : error.message || "Failed to publish exercise",
        icon: "error",
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
      });
    } finally {
      setPublishLoading(false);
    }
  };

  const handleCancel = () => {
    setIsChecked(false); // Reset checked state when canceling
    onClose();
  };

  const degreeOptions = ["1/2", "3/4", "5/6", "7/8"];
  const categoryOptions = categoriesData.categories.map((cat) => ({
    title: cat.title || "Unnamed",
    hasSubCategories: !!cat.sub_categories && cat.sub_categories.length > 0,
  }));
  const subCategoryOptions = publishSettings.category
    ? getSubCategories(publishSettings.category, 1)
    : [];
  const subSubCategoryOptions = publishSettings.sub_category
    ? getSubCategories(`${publishSettings.category}.${publishSettings.sub_category}`, 2)
    : [];
  const subSubSubCategoryOptions = publishSettings.sub_sub_category
    ? getSubCategories(
        `${publishSettings.category}.${publishSettings.sub_category}.${publishSettings.sub_sub_category}`,
        3
      )
    : [];

  // Reminds the user which exercise they're publishing, even if they leave
  // and come back to this modal later.
  const exerciseSummary = (exerciseName || exerciseType || exerciseImage) && (
    <div className="publish-modal-exercise-summary">
      {exerciseImage && (
        <img
          src={exerciseImage}
          alt={exerciseName || "Exercise preview"}
          className="publish-modal-exercise-image"
        />
      )}
      <div className="publish-modal-exercise-info">
        {exerciseName && (
          <p className="publish-modal-exercise-name">{exerciseName}</p>
        )}
        {exerciseType && (
          <span className="publish-modal-exercise-type">{exerciseType}</span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="publish-modal-overlay">
        <div className="publish-modal-loading">
          {exerciseSummary}
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="publish-modal-overlay">
        <div className="publish-modal-error-container">
          {exerciseSummary}
          <p className="publish-modal-error-text">Error: {error}</p>
          <button
            onClick={handleRetry}
            className="publish-modal-button publish-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="publish-modal-overlay">
      <div className="publish-modal-container">
        <h2 className="publish-modal-title">Publish Exercise</h2>
        {exerciseSummary}
        <form onSubmit={handleSubmit}>
          <div className="publish-modal-field">
            <label className="publish-modal-label">Degree</label>
            <select
              name="degree"
              value={publishSettings.degree}
              onChange={handleChange}
              className="publish-modal-select"
              required
            >
              {degreeOptions.map((degree) => (
                <option key={degree} value={degree}>
                  {degree}
                </option>
              ))}
            </select>
          </div>

          <div className="publish-modal-field">
            <label className="publish-modal-label">Category</label>
            <select
              name="category"
              value={publishSettings.category}
              onChange={handleChange}
              className="publish-modal-select"
              required
              disabled={!publishSettings.degree}
            >
              <option value="">Select Category</option>
              {categoryOptions.map((option) => (
                <option key={option.title} value={option.title}>
                  {option.title}
                </option>
              ))}
            </select>
          </div>

          <div className="publish-modal-field">
            <label className="publish-modal-label">Sub-Category</label>
            <select
              name="sub_category"
              value={publishSettings.sub_category}
              onChange={handleChange}
              className="publish-modal-select"
              disabled={!publishSettings.category || subCategoryOptions.length === 0}
              required
            >
              <option value="">Select Sub-Category</option>
              {subCategoryOptions.map((option) => (
                <option key={option.title} value={option.title}>
                  {option.title}
                </option>
              ))}
            </select>
          </div>

          <div className="publish-modal-field">
            <label className="publish-modal-label">Sub-Sub-Category</label>
            <select
              name="sub_sub_category"
              value={publishSettings.sub_sub_category}
              onChange={handleChange}
              className="publish-modal-select"
              disabled={!publishSettings.sub_category || subSubCategoryOptions.length === 0}
              required
            >
              <option value="">Select Sub-Sub-Category</option>
              {subSubCategoryOptions.map((option) => (
                <option key={option.title} value={option.title}>
                  {option.title}
                </option>
              ))}
            </select>
          </div>

          <div className="publish-modal-field">
            <label className="publish-modal-label">Sub-Sub-Sub-Category</label>
            <select
              name="sub_sub_sub_category"
              value={publishSettings.sub_sub_sub_category}
              onChange={handleChange}
              className="publish-modal-select"
              disabled={!publishSettings.sub_sub_category || subSubSubCategoryOptions.length === 0}
              required
            >
              <option value="">Select Sub-Sub-Sub-Category</option>
              {subSubSubCategoryOptions.map((option) => (
                <option key={option.title} value={option.title}>
                  {option.title}
                </option>
              ))}
            </select>
          </div>

          <div className="publish-modal-field">
            <label className="publish-modal-label">Objective (optional)</label>
            <textarea
              name="objective"
              value={publishSettings.objective}
              onChange={handleChange}
              className="publish-modal-textarea"
              placeholder="Describe the learning objective for this exercise..."
            />
          </div>

          {publishError && (
            <p className="publish-modal-error">{publishError}</p>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
            <button
              type="button"
              onClick={handleCancel}
              className="publish-modal-button cancel-button"
              disabled={publishLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="publish-modal-button publish-button"
              disabled={publishLoading}
            >
              {publishLoading ? "Publishing..." : "Publish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublishForm;